import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { environment } from '../../config/environment';

export interface UserSummary {
  id: string;
  email: string;
  username?: string;
  phone?: string;
  role: 'ADMIN' | 'TEACHER' | 'STUDENT' | 'PARENT';
  status: string;
  createdAt: string;
}

export interface LoginResponse {
  accessToken: string;
  tokenType: string;
  expiresIn: number;
  user: UserSummary;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly tokenKey = 'tsms_access_token';
  private readonly userKey = 'tsms_current_user';
  private readonly api = environment.apiBaseUrl;
  private logoutTimer: any | null = null;

  constructor(
    private http: HttpClient,
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    // Initialize auto-logout on app start based on stored token
    if (this.isBrowser()) {
      const token = this.getToken();
      if (token) {
        if (this.isTokenExpired(token)) {
          // Token already expired — clear and redirect to login
          this.clearToken();
          this.router.navigate(['/login']);
        } else {
          // Schedule automatic logout when token expires
          this.scheduleAutoLogoutFromToken(token);
        }
      }
    }
  }

  private isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  login(email: string, password: string) {
    return this.http.post<LoginResponse>(`${this.api}/api/v1/auth/login`, { email, password });
  }

  fetchMe() {
    return this.http.get<UserSummary>(`${this.api}/api/v1/auth/me`);
  }

  logout(navigateToLogin: boolean = true) {
    // Stateless on backend; we just drop the token.
    this.clearLogoutTimer();
    this.clearToken();
    if (navigateToLogin && this.isBrowser()) {
      this.router.navigate(['/login']);
    }
  }

  storeToken(token: string) {
    if (!this.isBrowser()) return;
    localStorage.setItem(this.tokenKey, token);
    // Schedule automatic logout when token expires
    this.scheduleAutoLogoutFromToken(token);
  }

  storeUser(user: UserSummary) {
    if (!this.isBrowser()) return;
    localStorage.setItem(this.userKey, JSON.stringify(user));
  }

  getToken(): string | null {
    if (!this.isBrowser()) return null;
    return localStorage.getItem(this.tokenKey);
  }

  getCurrentUser(): UserSummary | null {
    if (!this.isBrowser()) return null;
    const userStr = localStorage.getItem(this.userKey);
    if (!userStr) return null;
    try {
      return JSON.parse(userStr);
    } catch {
      return null;
    }
  }

  clearToken() {
    if (!this.isBrowser()) return;
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.userKey);
    this.clearLogoutTimer();
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) return false;
    if (this.isTokenExpired(token)) {
      // Proactively clear expired token
      this.clearToken();
      return false;
    }
    return true;
  }

  hasRole(role: string): boolean {
    const user = this.getCurrentUser();
    return user ? user.role === role : false;
  }

  getUserRole(): 'ADMIN' | 'TEACHER' | 'STUDENT' | 'PARENT' | null {
    const user = this.getCurrentUser();
    return user ? user.role : null;
  }

  isStudent(): boolean {
    return this.getUserRole() === 'STUDENT';
  }

  isAdmin(): boolean {
    return this.getUserRole() === 'ADMIN';
  }

  isTeacher(): boolean {
    return this.getUserRole() === 'TEACHER';
  }

  isParent(): boolean {
    return this.getUserRole() === 'PARENT';
  }

  // ===== JWT helpers and auto-logout scheduling =====

  private decodePayload(token: string): any | null {
    try {
      const payload = token.split('.')[1];
      if (!payload) return null;
      const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      return JSON.parse(decodeURIComponent(escape(json)));
    } catch {
      return null;
    }
  }

  isTokenExpired(token?: string): boolean {
    const t = token || this.getToken();
    if (!t) return true;
    const payload = this.decodePayload(t);
    const expSec: number | undefined = payload?.exp;
    if (!expSec || isNaN(expSec)) {
      // If exp is missing or invalid, assume expired/invalid
      return true;
    }
    const nowSec = Math.floor(Date.now() / 1000);
    // Add small clock skew of 5 seconds
    return nowSec >= (expSec - 5);
  }

  private scheduleAutoLogoutFromToken(token: string): void {
    const payload = this.decodePayload(token);
    const expSec: number | undefined = payload?.exp;
    if (!expSec || isNaN(expSec)) return;
    const expiresAtMs = expSec * 1000;
    this.scheduleAutoLogout(expiresAtMs);
  }

  private scheduleAutoLogout(expiresAtMs: number): void {
    if (!this.isBrowser()) return;
    this.clearLogoutTimer();
    const msUntilExpiry = Math.max(0, expiresAtMs - Date.now());
    this.logoutTimer = setTimeout(() => {
      this.logout(true);
    }, msUntilExpiry);
  }

  private clearLogoutTimer(): void {
    if (this.logoutTimer) {
      clearTimeout(this.logoutTimer);
      this.logoutTimer = null;
    }
  }
}